import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import NewsCard from '@/components/feature/NewsCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getHotNews, getSuggestions, searchNews } from '@/utils/newsUtils';
import { useSeo } from '@/hooks/useSeo';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q') || '';
  const [input, setInput] = useState(keyword);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchNews(keyword), [keyword]);
  const suggestions = useMemo(() => getSuggestions(input), [input]);
  const hot = useMemo(() => getHotNews(5), []);

  useSeo({
    title: keyword ? `搜索“${keyword}” - 吃瓜网` : '搜索 - 吃瓜网',
    description: keyword
      ? `在吃瓜网中搜索“${keyword}”，共找到 ${results.length} 条相关热点资讯与娱乐八卦内容。`
      : '在吃瓜网搜索最新的热点新闻、娱乐八卦与明星动态。',
    keywords: keyword ? `${keyword},吃瓜网,搜索,热点新闻` : '吃瓜网,搜索,热点新闻',
    canonicalPath: '/search',
  });

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setParams({ q });
    setFocused(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 py-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

          <section className="mt-5 max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">站内搜索</h1>
            <div className="mt-4 relative">
              <div className="flex items-center gap-2 h-12 rounded-lg border border-background-300 bg-background-50 px-4">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => window.setTimeout(() => setFocused(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submit(input);
                  }}
                  placeholder="输入标题、明星名或话题关键词"
                  className="flex-1 h-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
                <button
                  type="button"
                  onClick={() => submit(input)}
                  className="h-9 px-4 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>

              {focused && input.trim() && (
                <div className="absolute left-0 right-0 top-full mt-2 z-20 rounded-lg border border-background-300 bg-background-50 p-2 animate-fade-in">
                  <p className="px-3 pt-1 pb-2 text-xs text-foreground-400">联想建议（演示效果）</p>
                  {suggestions.length > 0 ? (
                    suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onMouseDown={() => submit(s.title)}
                        className="w-full text-left flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-search-line text-foreground-400"></i>
                        <span className="truncate">{s.title}</span>
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-foreground-400">暂无联想结果</p>
                  )}
                </div>
              )}
            </div>
          </section>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {keyword ? (
                <>
                  <p className="text-sm text-foreground-600">
                    共找到 <span className="font-semibold text-primary-600">{results.length}</span> 条与
                    “<span className="font-semibold text-foreground-900">{keyword}</span>”相关的结果
                  </p>

                  {results.length === 0 ? (
                    <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                      <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                      <p className="mt-4 text-base font-medium text-foreground-800">未找到相关内容</p>
                      <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或者看看下面的热门资讯</p>
                      <Link
                        to="/"
                        className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-home-4-line"></i>
                        返回首页
                      </Link>
                    </div>
                  ) : (
                    <div className="mt-5 flex flex-col gap-4">
                      {results.map((article) => (
                        <NewsCard key={article.id} article={article} layout="row" />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                  <i className="ri-search-line text-4xl text-foreground-300"></i>
                  <p className="mt-4 text-base font-medium text-foreground-800">输入关键词开始搜索</p>
                  <p className="mt-2 text-sm text-foreground-500">试试搜索明星名、话题或事件关键词</p>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-base font-bold font-heading text-foreground-950">热门资讯</h2>
                <ul className="mt-3 flex flex-col">
                  {hot.map((item, i) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/${item.id}`}
                        className="flex items-start gap-3 py-2.5 group cursor-pointer"
                      >
                        <span className="shrink-0 w-5 text-sm font-bold font-heading text-primary-500">{i + 1}</span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}